import React from 'react';

/**
 * A tilted 3D book mockup component with a bookmark ribbon.
 * Displays the user's cover image with title overlay on a realistic angled book.
 * Used in PreviewPage to drive PDF/print conversions.
 */
export default function TiltedBookMockup({ 
  coverImage, 
  title = 'Your Book',
  subtitle = '',
  year = '',
  size = 'md', // 'xs' | 'sm' | 'md' | 'lg'
}) {
  // Size configurations
  const sizeConfig = {
    xs: {
      wrapper: 'w-20 h-28',
      spine: 'w-2',
      pages: 'w-1.5',
      ribbon: 'w-1.5 h-5',
      ribbonBottom: '-bottom-2',
      shadow: 'blur-sm',
      titleSize: 'text-[8px]',
      subtitleSize: 'text-[6px]',
      yearSize: 'text-xs',
      padding: 'p-1.5',
    },
    sm: {
      wrapper: 'w-28 h-40',
      spine: 'w-3',
      pages: 'w-2',
      ribbon: 'w-2 h-7',
      ribbonBottom: '-bottom-3',
      shadow: 'blur-md',
      titleSize: 'text-[10px]',
      subtitleSize: 'text-[8px]',
      yearSize: 'text-sm',
      padding: 'p-2',
    },
    md: {
      wrapper: 'w-36 h-52',
      spine: 'w-4',
      pages: 'w-3',
      ribbon: 'w-3 h-9',
      ribbonBottom: '-bottom-4',
      shadow: 'blur-lg',
      titleSize: 'text-xs',
      subtitleSize: 'text-[9px]',
      yearSize: 'text-lg',
      padding: 'p-3',
    },
    lg: {
      wrapper: 'w-48 h-[17rem]',
      spine: 'w-5',
      pages: 'w-4',
      ribbon: 'w-4 h-11',
      ribbonBottom: '-bottom-5',
      shadow: 'blur-xl',
      titleSize: 'text-sm',
      subtitleSize: 'text-xs',
      yearSize: 'text-xl',
      padding: 'p-4',
    },
  };

  const config = sizeConfig[size] || sizeConfig.md;

  return (
    <div 
      className="relative inline-block"
      style={{ 
        perspective: '1200px',
        padding: '16px 24px 32px 16px',
      }}
    >
      {/* Book container with 3D transforms */}
      <div
        className={`relative ${config.wrapper}`}
        style={{
          transformStyle: 'preserve-3d',
          transform: 'rotateY(-15deg) rotateX(5deg) rotateZ(-2deg)',
          transition: 'transform 0.4s ease-out',
        }}
      >
        {/* Book spine (left side - gives depth) */}
        <div
          className={`absolute left-0 top-0 ${config.spine} h-full`}
          style={{
            background: 'linear-gradient(to right, #7C2D12, #9A3412, #B45309)',
            transform: 'rotateY(90deg)',
            transformOrigin: 'left center',
            boxShadow: 'inset -3px 0 8px rgba(0,0,0,0.4)',
            borderRadius: '2px 0 0 2px',
          }}
        >
          {/* Spine texture lines */}
          <div className="absolute inset-0 opacity-20">
            {[...Array(5)].map((_, i) => (
              <div 
                key={i} 
                className="w-full border-b border-white/30"
                style={{ height: `${100 / 6}%` }}
              />
            ))}
          </div>
        </div>

        {/* Book cover (front face) */}
        <div
          className="absolute inset-0 rounded-r-sm overflow-hidden"
          style={{
            boxShadow: `
              4px 4px 12px rgba(0,0,0,0.12),
              8px 8px 24px rgba(0,0,0,0.08),
              12px 12px 36px rgba(0,0,0,0.05)
            `,
            transform: 'translateZ(12px)',
            border: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          {/* Cover background - image or gradient */}
          {coverImage ? (
            <img
              src={coverImage}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div 
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(145deg, #DC2626 0%, #B91C1C 30%, #991B1B 70%, #7F1D1D 100%)',
              }}
            />
          )}

          {/* Dark overlay for text readability */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.2) 70%, rgba(0,0,0,0.5) 100%)',
            }}
          />

          {/* Title and info overlay */}
          <div className={`absolute inset-0 ${config.padding} flex flex-col justify-between text-white`}>
            {/* Top: Title area */}
            <div className="text-center pt-2">
              <h3 
                className={`font-display font-bold ${config.titleSize} leading-tight drop-shadow-lg`}
                style={{ 
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                  letterSpacing: '0.02em',
                }}
              >
                {title}
              </h3>
              {subtitle && (
                <p 
                  className={`${config.subtitleSize} text-white/80 mt-1 drop-shadow`}
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                >
                  {subtitle}
                </p>
              )}
            </div>

            {/* Bottom: Year */}
            {year && (
              <div className="text-center pb-1">
                <span 
                  className={`font-display font-bold ${config.yearSize} drop-shadow-lg`}
                  style={{ 
                    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                    letterSpacing: '0.05em',
                  }}
                >
                  {year}
                </span>
              </div>
            )}
          </div>

          {/* Decorative border frame */}
          <div 
            className="absolute inset-2 border border-white/20 rounded-sm pointer-events-none"
            style={{ boxShadow: 'inset 0 0 10px rgba(255,255,255,0.1)' }}
          />

          {/* Glossy reflection overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(130deg, rgba(255,255,255,0.3) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.1) 100%)',
            }}
          />

          {/* Top edge highlight */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-white/50 via-white/30 to-transparent" />
        </div>

        {/* Book pages (right edge) */}
        <div
          className={`absolute right-0 top-1 bottom-1 ${config.pages}`}
          style={{
            background: 'linear-gradient(to right, #F5F5F4, #FAFAF9, #F5F5F4)',
            transform: 'rotateY(-90deg) translateX(6px)',
            transformOrigin: 'right center',
            boxShadow: 'inset 2px 0 4px rgba(0,0,0,0.08)',
          }}
        >
          {/* Page lines */}
          {[...Array(12)].map((_, i) => (
            <div 
              key={i}
              className="border-b border-stone-200/60"
              style={{ height: `${100 / 13}%` }}
            />
          ))}
        </div>

        {/* Book pages (bottom edge) */}
        <div
          className="absolute bottom-0 left-2 right-1 h-2"
          style={{
            background: 'linear-gradient(to bottom, #FAFAF9, #E7E5E4, #F5F5F4)',
            transform: 'rotateX(-90deg) translateY(4px)',
            transformOrigin: 'bottom center',
            boxShadow: 'inset 0 2px 3px rgba(0,0,0,0.1)',
          }}
        />

        {/* Bookmark ribbon */}
        <div
          className={`absolute ${config.ribbon} ${config.ribbonBottom} right-[18%]`}
          style={{
            transform: 'translateZ(13px)',
          }}
        >
          <div
            className="w-full h-full"
            style={{
              background: 'linear-gradient(180deg, #EF4444 0%, #DC2626 50%, #B91C1C 100%)',
              clipPath: 'polygon(0 0, 100% 0, 100% 85%, 50% 100%, 0 85%)',
              boxShadow: '2px 3px 4px rgba(0,0,0,0.25)',
            }}
          />
        </div>
      </div>

      {/* Ground shadow */}
      <div
        className={`absolute bottom-2 left-1/2 w-[70%] h-4 bg-black/10 ${config.shadow} rounded-[50%]`}
        style={{
          transform: 'translateX(-45%)',
        }}
      />
    </div>
  );
}
